import React from 'react';
import { ArrowLeft, Calendar, MapPin, Trash2, Edit2, Package, Minus, Plus, ScrollText } from 'lucide-react';
import { InventoryItem } from '../hooks/useInventory';
interface ItemDetailProps {
  item: InventoryItem;
  onBack: () => void;
}
export function ItemDetail({
  item,
  onBack
}: ItemDetailProps) {
  const stockPercentage = Math.round(item.quantity / item.maxQuantity * 100);
  const daysToExpiry = Math.ceil((new Date(item.expiryDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
  const statusColor = item.status === 'Critical' ? 'text-[#8B0000] bg-[#8B0000]/10 border-[#8B0000]/20' : item.status === 'Warning' ? 'text-[#D4A017] bg-[#D4A017]/10 border-[#D4A017]/20' : 'text-[#2D5A3D] bg-[#2D5A3D]/10 border-[#2D5A3D]/20';
  const barColor = stockPercentage < 30 ? 'bg-[#8B0000]' : stockPercentage < 50 ? 'bg-[#D4A017]' : 'bg-[#2D5A3D]';
  return <div className="pb-24 min-h-screen" data-id="element-228">
      {/* Header */}
      <div className="bg-[#F5F0E1] border-b border-[#C9A227]/30 px-4 py-4 sticky top-0 z-10 shadow-sm" data-id="element-229">
        <div className="flex items-center justify-between max-w-md mx-auto" data-id="element-230">
          <button onClick={onBack} className="p-2 hover:bg-[#C9A227]/20 rounded-full transition-colors -ml-2" data-id="element-231">
            <ArrowLeft className="w-5 h-5 text-[#722F37]" data-id="element-232" />
          </button>
          <div className="flex gap-2" data-id="element-233">
            <button className="p-2 hover:bg-[#C9A227]/20 rounded-full transition-colors" data-id="element-234">
              <Edit2 className="w-5 h-5 text-[#722F37]" data-id="element-235" />
            </button>
            <button className="p-2 hover:bg-[#8B0000]/10 rounded-full transition-colors" data-id="element-236">
              <Trash2 className="w-5 h-5 text-[#8B0000]" data-id="element-237" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-8" data-id="element-238">
        {/* Item Image */}
        <div className="w-32 h-32 mx-auto mb-6 bg-[#FFFDF5] border-2 border-[#C9A227] rounded-2xl flex items-center justify-center overflow-hidden shadow-[0_4px_20px_rgba(201,162,39,0.2)] relative group" data-id="element-239">
          {/* Corner flourishes */}
          <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-[#C9A227]" data-id="element-240" />
          <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-[#C9A227]" data-id="element-241" />
          <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-[#C9A227]" data-id="element-242" />
          <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-[#C9A227]" data-id="element-243" />

          {item.photoUrl ? <img src={item.photoUrl} alt={item.name} className="w-full h-full object-cover sepia-[0.3]" data-id="element-244" /> : <Package className="w-12 h-12 text-[#C9A227]" data-id="element-245" />}
        </div>

        {/* Item Info */}
        <div className="text-center mb-8" data-id="element-246">
          <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-3 border ${statusColor} uppercase tracking-wider`} data-id="element-247">
            {item.status === 'Critical' ? 'Critical Levels' : item.status === 'Warning' ? 'Depleting' : 'Plentiful'}
          </span>
          <h1 className="text-3xl font-cinzel font-bold text-[#2C2C2C] mb-2 leading-tight" data-id="element-248">
            {item.name}
          </h1>
          <p className="text-[#722F37] font-serif italic" data-id="element-249">{item.category}</p>
        </div>

        {/* Quantity Control */}
        <div className="bg-[#FFFDF5] rounded-xl border border-[#C9A227] p-6 mb-6 shadow-sm relative overflow-hidden" data-id="element-250">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#C9A227]/20" data-id="element-251" />

          <div className="text-center mb-6" data-id="element-252">
            <div className="text-5xl font-cinzel font-bold text-[#2C2C2C] mb-1" data-id="element-253">
              {item.quantity}
            </div>
            <div className="text-[#722F37] font-serif italic" data-id="element-254">{item.unit}</div>
          </div>

          <div className="flex items-center justify-center gap-6" data-id="element-255">
            <button className="w-12 h-12 bg-[#F5F0E1] border border-[#C9A227]/40 rounded-full flex items-center justify-center active:bg-[#E6DCC3] transition-colors hover:border-[#C9A227]" data-id="element-256">
              <Minus className="w-5 h-5 text-[#722F37]" data-id="element-257" />
            </button>

            <div className="flex-1 max-w-32" data-id="element-258">
              <div className="h-2 bg-[#F5F0E1] border border-[#C9A227]/20 rounded-full overflow-hidden" data-id="element-259">
                <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{
                width: `${stockPercentage}%`
              }} data-id="element-260" />
              </div>
              <div className="text-xs text-[#555] text-center mt-2 font-serif" data-id="element-261">
                {stockPercentage}% of {item.maxQuantity}
              </div>
            </div>

            <button className="w-12 h-12 bg-[#722F37] text-[#F5F0E1] rounded-full flex items-center justify-center active:scale-95 transition-transform shadow-lg shadow-[#722F37]/30 border border-[#C9A227] hover:bg-[#8B3A44]" data-id="element-262">
              <Plus className="w-5 h-5" data-id="element-263" />
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="bg-[#FFFDF5] rounded-xl border border-[#C9A227]/40 divide-y divide-[#C9A227]/10 shadow-sm" data-id="element-264">
          <div className="flex items-center p-4" data-id="element-265">
            <div className="w-10 h-10 bg-[#F5F0E1] rounded-lg border border-[#C9A227]/20 flex items-center justify-center mr-4" data-id="element-266">
              <MapPin className="w-5 h-5 text-[#722F37]" data-id="element-267" />
            </div>
            <div data-id="element-268">
              <div className="text-xs text-[#722F37] uppercase tracking-widest font-bold mb-0.5" data-id="element-269">
                Location
              </div>
              <div className="font-cinzel font-bold text-[#2C2C2C]" data-id="element-270">
                {item.zone}
              </div>
            </div>
          </div>

          <div className="flex items-center p-4" data-id="element-271">
            <div className="w-10 h-10 bg-[#F5F0E1] rounded-lg border border-[#C9A227]/20 flex items-center justify-center mr-4" data-id="element-272">
              <Calendar className="w-5 h-5 text-[#722F37]" data-id="element-273" />
            </div>
            <div data-id="element-274">
              <div className="text-xs text-[#722F37] uppercase tracking-widest font-bold mb-0.5" data-id="element-275">
                Expires
              </div>
              <div className={`font-cinzel font-bold ${daysToExpiry <= 7 ? 'text-[#8B0000]' : 'text-[#2C2C2C]'}`} data-id="element-276">
                {daysToExpiry <= 0 ? 'Expired!' : daysToExpiry === 1 ? 'Tomorrow' : `In ${daysToExpiry} days`}
              </div>
            </div>
          </div>
        </div>

        {item.notes && <div className="bg-[#FFFDF5] rounded-xl border border-[#C9A227]/40 p-5 mt-4 shadow-sm relative" data-id="element-277">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#C9A227]/40" data-id="element-278" />
            <div className="flex items-center gap-2 mb-2" data-id="element-279">
              <ScrollText className="w-4 h-4 text-[#722F37]" data-id="element-280" />
              <div className="text-xs text-[#722F37] uppercase tracking-widest font-bold" data-id="element-281">
                Notes
              </div>
            </div>
            <p className="text-[#2C2C2C] font-serif italic leading-relaxed" data-id="element-282">
              {item.notes}
            </p>
          </div>}
      </div>
    </div>;
}